import { useState, useEffect } from "react";
import { useAdmin } from "@/context/AdminContext";
import { Package, FolderOpen, ShoppingCart, TrendingUp, DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const AdminDashboard = () => {
  const { apiGet } = useAdmin();
  const [stats, setStats] = useState({
    products: 0,
    categories: 0,
    orders: 0,
    revenue: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [products, categories, orders] = await Promise.all([
          apiGet("/products"),
          apiGet("/categories"),
          apiGet("/admin/orders"),
        ]);

        const revenue = orders
          .filter((o) => o.payment_status === "paid")
          .reduce((sum, o) => sum + o.total, 0);

        setStats({
          products: products.length,
          categories: categories.length,
          orders: orders.length,
          revenue,
        });

        setRecentOrders(orders.slice(0, 5));
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiGet]);

  const statCards = [
    { title: "Total Products", value: stats.products, icon: Package, color: "text-blue-600 bg-blue-100" },
    { title: "Categories", value: stats.categories, icon: FolderOpen, color: "text-purple-600 bg-purple-100" },
    { title: "Total Orders", value: stats.orders, icon: ShoppingCart, color: "text-orange-600 bg-orange-100" },
    { title: "Revenue", value: `₹${stats.revenue.toLocaleString()}`, icon: DollarSign, color: "text-green-600 bg-green-100" },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back to your store management panel</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title} data-testid={`stat-${stat.title.toLowerCase().replace(" ", "-")}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Recent Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentOrders.length > 0 ? (
            <div className="space-y-4">
              {recentOrders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                  data-testid={`order-${order.id}`}
                >
                  <div>
                    <p className="font-medium">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground">
                      {order.items.length} items • {order.delivery_address.city}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">₹{order.total.toLocaleString()}</p>
                    <span className={`status-badge status-${order.order_status}`}>
                      {order.order_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No orders yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
